import { useQuery, UseQueryResult } from "@tanstack/react-query"

import { parseFrontmatter } from "features/Docs/utils/frontmatter"
import { DocsParsed } from "features/Docs/types/docs.types"

/** Map from a content glob key (e.g. "./format-guide/overview.md") to its parse. */
export type DocsContentMap = Map<string, DocsParsed>

/**
 * Load the markdown content chunk once and parse each file's frontmatter.
 * Content is bundled via `import.meta.glob`, so the dynamic import resolves a
 * single lazy chunk; parsing happens exactly once and is cached forever.
 */
const loadDocsContent = async (): Promise<DocsContentMap> => {
  const module = await import("features/Docs/content")
  const raw = module.default
  const map: DocsContentMap = new Map()
  Object.entries(raw).forEach(([key, text]) => {
    map.set(key, parseFrontmatter(text))
  })
  return map
}

/** React-query hook exposing the parsed docs content map. */
export const useDocsContent = (): UseQueryResult<DocsContentMap> =>
  useQuery({
    queryKey: ["docs-content"],
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: loadDocsContent,
  })
